import type { EntryCta, FiatProvider } from '@funkit/connect-core'
import {
  PAYMENT_SESSION_ENDPOINT,
  PaymentMethod,
  submittableTransition,
} from '@funkit/connect-core'
import { useEffect, useRef } from 'react'

import { logger } from '../logging/logger'

export function useFiatCheckoutPreparation({
  provider,
  paymentMethod,
  entryCta,
  quoteRef,
  ready,
  prepare,
}: {
  paymentMethod: string
  provider: FiatProvider | undefined
  entryCta: EntryCta
  quoteRef: string | undefined
  ready: boolean
  prepare: () => void
}): boolean {
  const attempted = useRef(new Set<string>())
  const transition = submittableTransition(entryCta)
  // Which providers get the tapless preparation is decided here because the
  // session response is the first thing that says how the provider captures:
  // - EMBEDDED draws its own Apple Pay button in the slot, so the tap is still
  //   the user's and the session can be minted ahead of it
  // - HANDOFF (Banxa) opens the browser on sight, so its session must wait
  //   for the deposit tap
  const preparesEmbeddedCheckout =
    (provider === 'COINBASE' || provider === 'MOONPAY') &&
    paymentMethod === PaymentMethod.APPLE_PAY &&
    entryCta.kind === 'PAY' &&
    transition?.endpoint === PAYMENT_SESSION_ENDPOINT &&
    Object.values(transition.inputs ?? {}).every(
      (input) => input.type === 'TERMS_ACCEPTANCE',
    )

  useEffect(() => {
    if (
      !preparesEmbeddedCheckout ||
      !ready ||
      quoteRef === undefined ||
      attempted.current.has(quoteRef)
    ) {
      return
    }
    attempted.current.add(quoteRef)
    logger.info('connect-rn:fiat:checkout_preparing', {
      provider,
      quoteRef,
      acceptanceSource: 'checkout_preparation',
    })
    prepare()
  }, [preparesEmbeddedCheckout, ready, quoteRef, provider, prepare])

  return preparesEmbeddedCheckout
}
